import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Paper,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  Snackbar,
  Alert,
  InputAdornment,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Chip,
  IconButton,
  styled,
  Autocomplete,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import AdminApi from "../../components/AdminApi/Api/api";
import { Country, State, City } from "country-state-city";
import * as XLSX from "xlsx";

// Styled Components
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const FormSectionHeader = styled(CardContent)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const FormSectionBody = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    paddingLeft: theme.spacing(1.5),
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiSelect-select": {
    paddingLeft: theme.spacing(1.5),
  },
}));

const styles = {
  formControl: {
    width: "100%",
    marginBottom: "16px",
  },
  input: {
    width: "100%",
  },
  sectionTitle: {
    marginBottom: "16px",
    fontWeight: "bold",
  },
  addButton: {
    marginBottom: "16px",
  },
  submitButton: {
    marginTop: "24px",
    padding: "12px 24px",
    fontSize: "16px",
  },
  serviceRow: {
    alignItems: "center",
    marginBottom: "16px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    borderRadius: "4px",
  },
  removeButton: {
    marginLeft: "auto",
  },
  menuItem: {
    whiteSpace: "normal",
    display: "block",
    padding: (theme) => theme.spacing(1, 2),
  },
};

// Initial State
const initialFormState = {
  clientName: "",
  productName: "",
  primaryServices: [],
  primaryServiceIds: [],
  subCategories: [],
  selectedSubCategories: [],
  serviceAreas: {
    categories: [],
    states: [],
    cities: [],
    pincodes: [],
  },
  specificRequirement: "",
  shortCode: "",
  officialEmail: "",
  clientPocPersonName: "",
  clientPocPersonDesignation: "",
  clientPocPhone: "",
  addressLine: "",
  addressCity: "",
  addressState: "",
  addressStateCode: "",
  addressPincode: "",
  country: "India",
  gstNumber: "",
  aadhaarNumber: "",
  panNumber: "",
  bankName: "",
  accountNumber: "",
  branch: "",
  ifscCode: "",
  tinNumber: "",
  selectedCategories: [],
  serviceStateCodes: [],
  serviceStates: [],
  serviceCities: [],
  servicePincode: "",
};

// Helper function to map service areas to subcategories
const mapServiceAreasToSubCategories = (serviceAreas, subCategoriesList) => {
  if (!serviceAreas || !serviceAreas.categories) return [];

  return serviceAreas.categories.map((category) => {
    const subCategoryInfo = subCategoriesList.find(
      (cat) => cat._id === category.category_id
    );

    return {
      category_id: category.category_id,
      name: subCategoryInfo?.name || "",
      name2: subCategoryInfo?.name2 || "",
      payment_way: {
        isPayViaClient: category.payment_way?.isPayViaClient || false,
        isPayViaCustomer: category.payment_way?.isPayViaCustomer || true,
      },
      ticketPicPrize: category.payment_way?.ticketPicPrize || 0,
    };
  });
};

// Add this function before the component
const mapClientToFormData = async (client, allPrimaryServices) => {
  // Get the primary service IDs from the client data
  const primaryServiceIds = client.serviceAreas?.primary_services || [];

  const primaryServices = primaryServiceIds
    .map((id) => {
      const service = allPrimaryServices.find((s) => s.id === id);
      return service ? service.name : "";
    })
    .filter((name) => name !== "");

  // Fetch subcategories for all primary services
  let allSubCategories = [];
  for (const serviceId of primaryServiceIds) {
    try {
      const res = await new AdminApi().getaddedServiceCategory(serviceId);
      const subCategories =
        res.data.data?.names?.map((item) => ({
          _id: item._id,
          id: item._id,
          name: item.label,
          name2: item.label2,
          price: item.category_service_prize,
          primaryServiceId: serviceId,
        })) || [];

      allSubCategories = [...allSubCategories, ...subCategories];
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
    }
  }

  // Map the selected subcategories
  const selectedSubCategories = mapServiceAreasToSubCategories(
    client.serviceAreas,
    allSubCategories
  );

  // Get service state codes
  const serviceStates = client.serviceAreas?.states || [];
  const serviceStateCodes = serviceStates
    .map((state) => {
      const stateObj = State.getStatesOfCountry("IN").find(
        (s) => s.name === state
      );
      return stateObj ? stateObj.isoCode : "";
    })
    .filter((code) => code !== "");

  // Get address state code
  const addressStateObj = State.getStatesOfCountry("IN").find(
    (state) => state.name === client.address_details?.state
  );
  const addressStateCode = addressStateObj ? addressStateObj.isoCode : "";

  return {
    clientName: client.client_name || "",
    productName: client.product_name || "",
    primaryServices: primaryServices,
    primaryServiceIds: primaryServiceIds,
    subCategories: allSubCategories,
    selectedSubCategories: selectedSubCategories,
    serviceAreas: {
      categories: client.serviceAreas?.categories || [],
      states: client.serviceAreas?.states || [],
      cities: client.serviceAreas?.cities || [],
      pincodes: client.serviceAreas?.pincodes || [],
    },
    specificRequirement: client.specific_requirement || "",
    shortCode: client.short_code || "",
    officialEmail: client.official_email || "",
    clientPocPersonName: client.client_poc?.person_name || "",
    clientPocPersonDesignation: client.client_poc?.person_designation || "",
    clientPocPhone: client.client_poc?.phone?.mobile_number || "",
    addressLine: client.address_details?.address_line || "",
    addressCity: client.address_details?.city || "",
    addressState: client.address_details?.state || "",
    addressStateCode: addressStateCode,
    addressPincode: client.address_details?.pincode || "",
    country: client.address_details?.country || "India",
    gstNumber: client.gst_number || "",
    aadhaarNumber: client.aadhaar_number || "",
    panNumber: client.pan_number || "",
    bankName: client.bank_details?.bank_name || "",
    accountNumber: client.bank_details?.bank_account_number || "",
    branch: client.bank_details?.branch || "",
    ifscCode: client.bank_details?.ifsc_code || "",
    tinNumber: client.tin_number || "",
    selectedCategories: [],
    serviceStateCodes: serviceStateCodes,
    serviceStates: serviceStates,
    serviceCities: client.serviceAreas?.cities || [],
    servicePincode: client.serviceAreas?.pincodes?.[0] || "",
  };
};

const AddClient = ({
  isFormVisible,
  onToggleForm,
  onClientAdded,
  editingClient,
  isEditMode,
  onCancelEdit,
}) => {
  const [formData, setFormData] = useState(initialFormState);
  const [allPrimaryServices, setAllPrimaryServices] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState({
    primaryServices: false,
    subCategories: false,
    pincodes: false,
    cities: false,
    formData: false,
  });
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    type: "",
  });
  const [errors, setErrors] = useState({});
  const [bulkPincodeFile, setBulkPincodeFile] = useState(null);
  const [uploadedPincodeFileName, setUploadedPincodeFileName] = useState("");
  const [isUploadingPincodes, setIsUploadingPincodes] = useState(false);
  const [bulkUploadSummary, setBulkUploadSummary] = useState(null);

  const indianStates = State.getStatesOfCountry("IN");

  // Get cities for multiple states
  const getCitiesForStates = (stateCodes) => {
    let allCities = [];
    stateCodes.forEach((stateCode) => {
      const cities = City.getCitiesOfState("IN", stateCode);
      allCities = [...allCities, ...cities];
    });
    return allCities;
  };

  // Fetch all active services on mount and when edit mode changes
  useEffect(() => {
    const fetchActiveServices = async () => {
      setIsLoading((prev) => ({ ...prev, primaryServices: true }));
      try {
        const res = await new CenterRegistrationApi().getAllServices();

        if (res.data && Array.isArray(res.data)) {
          const services = res.data.map((service) => ({
            id: service._id,
            name: service.service_name,
          }));
          setAllPrimaryServices(services);

          // If we're in edit mode, load the client data after services are loaded
          if (isEditMode && editingClient) {
            setIsLoading((prev) => ({ ...prev, formData: true }));
            try {
              const mappedData = await mapClientToFormData(
                editingClient,
                services
              );
              setFormData(mappedData);
            } catch (err) {
              console.error("Error mapping client data:", err);
              showToast("error", "Failed to load client data");
            } finally {
              setIsLoading((prev) => ({ ...prev, formData: false }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching primary services:", err);
        showToast("error", "Failed to load services");
      } finally {
        setIsLoading((prev) => ({ ...prev, primaryServices: false }));
      }
    };

    fetchActiveServices();
  }, [editingClient, isEditMode]);

  // Reset form when switching from edit to add mode
  useEffect(() => {
    if (!isEditMode) {
      setFormData(initialFormState);
      setPincodeOptions([]);
      setBulkPincodeFile(null);
      setUploadedPincodeFileName("");
      setBulkUploadSummary(null);
    }
  }, [isEditMode]);

  // Fetch pincodes when service cities change
  useEffect(() => {
    const fetchPincodesForCities = async () => {
      if (
        formData.serviceCities.length > 0 &&
        formData.serviceStates.length > 0
      ) {
        setIsLoading((prev) => ({ ...prev, pincodes: true }));
        try {
          let allPincodes = [];

          for (const cityName of formData.serviceCities) {
            try {
              // Find which state this city belongs to
              const cityState = formData.serviceStates.find((state) => {
                const citiesInState = getCitiesForStates(
                  [
                    State.getStatesOfCountry("IN").find((s) => s.name === state)
                      ?.isoCode,
                  ].filter(Boolean)
                );
                return citiesInState.some((city) => city.name === cityName);
              });

              if (cityState) {
                const res =
                  await new AdminApi().getpincodesbasedontheselectstateandcity({
                    city: cityName,
                    state: cityState,
                  });

                if (res.data && Array.isArray(res.data)) {
                  allPincodes = [...allPincodes, ...res.data];
                }
              }
            } catch (err) {
              console.error(
                `Error fetching pincodes for city ${cityName}:`,
                err
              );
            }
          }

          // Remove duplicates based on Pincode
          const uniquePincodes = [
            ...new Map(
              allPincodes.map((item) => [item.Pincode, item])
            ).values(),
          ];

          setPincodeOptions(uniquePincodes);
        } catch (err) {
          console.error("Error fetching pincodes:", err);
          showToast("error", "Failed to load pincodes");
        } finally {
          setIsLoading((prev) => ({ ...prev, pincodes: false }));
        }
      } else {
        setPincodeOptions([]);
        setFormData((prev) => ({
          ...prev,
          serviceAreas: {
            ...prev.serviceAreas,
            pincodes: [],
          },
        }));
      }
    };

    fetchPincodesForCities();
  }, [formData.serviceCities, formData.serviceStates]);

  // Helper functions
  const showToast = (type, msg) => {
    setToast({
      open: true,
      msg,
      type,
    });
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 5000);
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const extractClientIdFromResponse = (response) => {
    const possibleIds = [
      response?._id,
      response?.clientId,
      response?.client_id,
      response?.client?._id,
      response?.data?._id,
      response?.data?.clientId,
      response?.data?.client_id,
      response?.data?.client?._id,
    ];

    return possibleIds.find((id) => typeof id === "string" && id.trim() !== "");
  };

  const uploadServiceAreasFromFile = async (clientId, pincodeFile) => {
    if (!clientId || !pincodeFile) return false;

    setIsUploadingPincodes(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("pincodeFile", pincodeFile);

      const response = await new AdminApi().uploadClientServiceAreas(
        clientId,
        uploadFormData
      );
      const uploadedData = response?.data || {};
      const details = response?.details || {};
      const summary = response?.summary || {};

      const updatedStates = Array.isArray(uploadedData.states)
        ? uploadedData.states
        : [];
      const updatedCities = Array.isArray(uploadedData.cities)
        ? uploadedData.cities
        : [];
      const updatedPincodes = Array.isArray(uploadedData.pincodes)
        ? uploadedData.pincodes
        : [];

      const updatedStateCodes = updatedStates
        .map((stateName) => {
          const stateObj = indianStates.find((state) => state.name === stateName);
          return stateObj?.isoCode || "";
        })
        .filter(Boolean);

      setFormData((prev) => ({
        ...prev,
        serviceStateCodes: updatedStateCodes,
        serviceStates: updatedStates,
        serviceCities: updatedCities,
        serviceAreas: {
          ...prev.serviceAreas,
          states: updatedStates,
          cities: updatedCities,
          pincodes: updatedPincodes,
        },
      }));

      setPincodeOptions(
        updatedPincodes.map((pin, index) => ({
          _id: `uploaded-${pin}-${index}`,
          Pincode: pin,
        }))
      );

      setBulkUploadSummary({
        totalInExcel: summary?.totalInExcel || 0,
        uploadedCount: summary?.uploadedCount || 0,
        duplicateCount: summary?.duplicateCount || 0,
        invalidCount: summary?.invalidCount || 0,
        uploadedPincodes: details?.uploadedPincodes || [],
        duplicatePincodes: details?.duplicatePincodes || [],
        invalidPincodes: details?.invalidPincodes || [],
      });

      setUploadedPincodeFileName(pincodeFile.name);
      showToast("success", response?.message || "Pincodes uploaded successfully");
      return true;
    } catch (err) {
      console.error("Error uploading service area pincodes:", err);
      showToast(
        "error",
        err?.response?.data?.message || "Failed to upload pincodes file"
      );
      setBulkUploadSummary(null);
      return false;
    } finally {
      setIsUploadingPincodes(false);
    }
  };

  const handleBulkPincodeFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      setBulkPincodeFile(null);
      return;
    }

    const isXlsxFile = selectedFile.name.toLowerCase().endsWith(".xlsx");
    if (!isXlsxFile) {
      showToast("error", "Please upload only .xlsx file");
      setBulkPincodeFile(null);
      e.target.value = "";
      return;
    }

    setBulkPincodeFile(selectedFile);
    setUploadedPincodeFileName("");
    setBulkUploadSummary(null);
  };

  const handleDownloadPincodeSample = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [
      { pincode: "560001" },
      { pincode: "110001" },
      { pincode: "400001" },
      { pincode: "700001" },
    ];
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Pincodes");
    XLSX.writeFile(workbook, "pincode-upload-sample.xlsx");
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for pincode
    if (
      (name === "addressPincode" || name === "servicePincode") &&
      value.length > 6
    )
      return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrimaryServiceChange = async (event) => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value
    const selectedIds = typeof value === "string" ? value.split(",") : value;

    // Ensure we're getting the service names as strings
    const selectedServices = allPrimaryServices
      .filter((service) => selectedIds.includes(service.id))
      .map((service) => service.name); // Extract just the names as strings

    setFormData((prev) => ({
      ...prev,
      primaryServiceIds: selectedIds,
      primaryServices: selectedServices, // This should be array of strings
      subCategories: [],
      selectedSubCategories: [],
    }));

    // Fetch subcategories for all selected services
    if (selectedIds.length > 0) {
      setIsLoading((prev) => ({ ...prev, subCategories: true }));
      try {
        let allSubCategories = [];

        for (const serviceId of selectedIds) {
          const res = await new AdminApi().getaddedServiceCategory(serviceId);
          const subCategories =
            res.data.data?.names?.map((item) => ({
              _id: item._id,
              id: item._id,
              name: item.label,
              name2: item.label2,
              price: item.category_service_prize,
              primaryServiceId: serviceId,
            })) || [];

          allSubCategories = [...allSubCategories, ...subCategories];
        }

        setFormData((prev) => ({
          ...prev,
          subCategories: allSubCategories,
        }));
      } catch (err) {
        console.error("Error fetching sub-categories:", err);
        showToast("error", "Failed to load sub-categories");
      } finally {
        setIsLoading((prev) => ({ ...prev, subCategories: false }));
      }
    }
  };

  const handleSubCategoryChange = (e, index) => {
    const selectedId = e.target.value;
    const selectedSubCategory = formData.subCategories.find(
      (cat) => cat._id === selectedId
    );

    const updatedSubCategories = [...formData.selectedSubCategories];
    updatedSubCategories[index] = {
      ...updatedSubCategories[index],
      category_id: selectedId,
      name: selectedSubCategory?.name || "",
      name2: selectedSubCategory?.name2 || "",
      primaryServiceId: selectedSubCategory?.primaryServiceId || "",
    };

    setFormData((prev) => ({
      ...prev,
      selectedSubCategories: updatedSubCategories,
    }));
  };

  const handlePriceChange = (e, index) => {
    const value = e.target.value;
    const numValue = value === "" ? "" : Number(value);

    const updatedSubCategories = [...formData.selectedSubCategories];
    updatedSubCategories[index] = {
      ...updatedSubCategories[index],
      ticketPicPrize: numValue,
    };
    setFormData((prev) => ({
      ...prev,
      selectedSubCategories: updatedSubCategories,
    }));
  };

  const handlePaymentMethodChange = (e, index) => {
    const updatedSubCategories = [...formData.selectedSubCategories];
    updatedSubCategories[index] = {
      ...updatedSubCategories[index],
      payment_way: {
        isPayViaClient: e.target.value === "client",
        isPayViaCustomer: e.target.value === "customer",
      },
    };
    setFormData((prev) => ({
      ...prev,
      selectedSubCategories: updatedSubCategories,
    }));
  };

  // Updated Service States handler with multi-select
  const handleServiceStatesChange = (event) => {
    const {
      target: { value },
    } = event;

    // When value is "select-all-states", select all states
    if (value[value.length - 1] === "select-all-states") {
      const allStateCodes = indianStates.map((state) => state.isoCode);
      const allStateNames = indianStates.map((state) => state.name);
      setFormData((prev) => ({
        ...prev,
        serviceStateCodes: allStateCodes,
        serviceStates: allStateNames,
        serviceCities: [],
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      }));
      setPincodeOptions([]);
      return;
    }

    // When value is "clear-all-states", clear all states
    if (value[value.length - 1] === "clear-all-states") {
      setFormData((prev) => ({
        ...prev,
        serviceStateCodes: [],
        serviceStates: [],
        serviceCities: [],
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      }));
      setPincodeOptions([]);
      return;
    }

    // Regular multi-select handling - filter out special values
    const selectedStateCodes = value.filter(
      (val) => val !== "select-all-states" && val !== "clear-all-states"
    );

    const selectedStateNames = selectedStateCodes
      .map((stateCode) => {
        const state = indianStates.find((s) => s.isoCode === stateCode);
        return state ? state.name : "";
      })
      .filter((name) => name !== "");

    setFormData((prev) => ({
      ...prev,
      serviceStateCodes: selectedStateCodes,
      serviceStates: selectedStateNames,
      serviceCities: [],
      serviceAreas: {
        ...prev.serviceAreas,
        pincodes: [],
      },
    }));
    setPincodeOptions([]);
  };

  const handleAddressStateChange = async (e) => {
    const stateCode = e.target.value;
    const selectedState = indianStates.find(
      (state) => state.isoCode === stateCode
    );

    setFormData((prev) => ({
      ...prev,
      addressState: selectedState?.name || "",
      addressStateCode: stateCode,
      addressCity: "",
    }));
  };

  // Updated Cities handler with proper multi-select
  const handleServiceCitiesChange = (event) => {
    const {
      target: { value },
    } = event;

    // When value is "select-all-cities", select all cities
    if (value[value.length - 1] === "select-all-cities") {
      const allCities = getCitiesForStates(formData.serviceStateCodes).map(
        (city) => city.name
      );
      setFormData((prev) => ({
        ...prev,
        serviceCities: allCities,
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      }));
      return;
    }

    // When value is "clear-all-cities", clear all cities
    if (value[value.length - 1] === "clear-all-cities") {
      setFormData((prev) => ({
        ...prev,
        serviceCities: [],
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      }));
      return;
    }

    // Regular multi-select handling - filter out special values
    const selectedCities = value.filter(
      (val) => val !== "select-all-cities" && val !== "clear-all-cities"
    );

    setFormData((prev) => ({
      ...prev,
      serviceCities: selectedCities,
      serviceAreas: {
        ...prev.serviceAreas,
        pincodes: [],
      },
    }));
  };

  // Updated Pincodes handler with proper multi-select
  const handlePincodeChange = (event) => {
    const {
      target: { value },
    } = event;

    // When value is "select-all-pincodes", select all pincodes
    if (value[value.length - 1] === "select-all-pincodes") {
      const allPincodes = pincodeOptions.map((pincode) => pincode.Pincode);
      setFormData((prev) => ({
        ...prev,
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: allPincodes,
        },
      }));
      return;
    }

    // When value is "clear-all-pincodes", clear all pincodes
    if (value[value.length - 1] === "clear-all-pincodes") {
      setFormData((prev) => ({
        ...prev,
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      }));
      return;
    }

    // Regular multi-select handling - filter out special values
    const selectedPincodes = value.filter(
      (val) => val !== "select-all-pincodes" && val !== "clear-all-pincodes"
    );

    setFormData((prev) => ({
      ...prev,
      serviceAreas: {
        ...prev.serviceAreas,
        pincodes: selectedPincodes,
      },
    }));
  };

  // Handle individual state checkbox changes
  const handleStateCheckboxChange = (stateCode) => (event) => {
    event.stopPropagation();

    const isChecked = event.target.checked;
    setFormData((prev) => {
      let updatedStateCodes;
      let updatedStateNames = [...prev.serviceStates];

      const state = indianStates.find((s) => s.isoCode === stateCode);

      if (isChecked) {
        updatedStateCodes = [...prev.serviceStateCodes, stateCode];
        if (state && !updatedStateNames.includes(state.name)) {
          updatedStateNames.push(state.name);
        }
      } else {
        updatedStateCodes = prev.serviceStateCodes.filter(
          (code) => code !== stateCode
        );
        if (state) {
          updatedStateNames = updatedStateNames.filter(
            (name) => name !== state.name
          );
        }
      }

      return {
        ...prev,
        serviceStateCodes: updatedStateCodes,
        serviceStates: updatedStateNames,
        serviceCities: [],
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      };
    });
    setPincodeOptions([]);
  };

  // Handle individual city checkbox changes
  const handleCityCheckboxChange = (cityName) => (event) => {
    event.stopPropagation();

    const isChecked = event.target.checked;
    setFormData((prev) => {
      let updatedCities;

      if (isChecked) {
        updatedCities = [...prev.serviceCities, cityName];
      } else {
        updatedCities = prev.serviceCities.filter((city) => city !== cityName);
      }

      return {
        ...prev,
        serviceCities: updatedCities,
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: [],
        },
      };
    });
  };

  // Handle individual pincode checkbox changes
  const handlePincodeCheckboxChange = (pincode) => (event) => {
    event.stopPropagation();

    const isChecked = event.target.checked;
    setFormData((prev) => {
      let updatedPincodes;

      if (isChecked) {
        updatedPincodes = [...prev.serviceAreas.pincodes, pincode];
      } else {
        updatedPincodes = prev.serviceAreas.pincodes.filter(
          (p) => p !== pincode
        );
      }

      return {
        ...prev,
        serviceAreas: {
          ...prev.serviceAreas,
          pincodes: updatedPincodes,
        },
      };
    });
  };

  const addSubCategory = () => {
    setFormData((prev) => ({
      ...prev,
      selectedSubCategories: [
        ...prev.selectedSubCategories,
        {
          category_id: "",
          payment_way: {
            isPayViaClient: false,
            isPayViaCustomer: true,
          },
          ticketPicPrize: "",
          primaryServiceId: "",
        },
      ],
    }));
  };

  const removeSubCategory = (index) => {
    setFormData((prev) => {
      const updatedSubCategories = [...prev.selectedSubCategories];
      updatedSubCategories.splice(index, 1);
      return { ...prev, selectedSubCategories: updatedSubCategories };
    });
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.clientName) newErrors.clientName = "Client name is required";
    if (!formData.productName)
      newErrors.productName = "Product name is required";
    if (formData.primaryServiceIds.length === 0)
      newErrors.primaryServices = "At least one primary service is required";
    if (formData.selectedSubCategories.length === 0)
      newErrors.subCategories = "At least one service is required";

    formData.selectedSubCategories.forEach((subCat, index) => {
      if (!subCat.category_id) {
        newErrors[`subCategory-${index}`] = "Sub-category is required";
      }
      if (
        subCat.ticketPicPrize === null ||
        subCat.ticketPicPrize === undefined ||
        subCat.ticketPicPrize === ""
      ) {
        newErrors[`price-${index}`] = "Price is required";
      } else if (isNaN(Number(subCat.ticketPicPrize))) {
        newErrors[`price-${index}`] = "Price must be a valid number";
      } else if (Number(subCat.ticketPicPrize) < 0) {
        newErrors[`price-${index}`] = "Price cannot be negative";
      }
      if (
        !subCat.payment_way.isPayViaClient &&
        !subCat.payment_way.isPayViaCustomer
      ) {
        newErrors[`paymentMethod-${index}`] = "Payment method is required";
      }
    });

    if (!formData.officialEmail)
      newErrors.officialEmail = "Official email is required";
    if (!formData.clientPocPersonName)
      newErrors.clientPocPersonName = "Contact person name is required";
    if (!formData.clientPocPhone)
      newErrors.clientPocPhone = "Contact phone is required";
    if (!formData.addressLine) newErrors.addressLine = "Address is required";
    if (!formData.addressState) newErrors.addressState = "State is required";
    if (!formData.addressCity) newErrors.addressCity = "City is required";
    if (!formData.addressPincode)
      newErrors.addressPincode = "Pincode is required";
    if (!formData.panNumber) newErrors.panNumber = "PAN number is required";
    if (!formData.bankName) newErrors.bankName = "Bank name is required";
    if (!formData.accountNumber)
      newErrors.accountNumber = "Account number is required";
    if (!formData.ifscCode) newErrors.ifscCode = "IFSC code is required";

    // Service area validation
    if (formData.serviceStateCodes.length === 0)
      newErrors.serviceStates = "At least one state is required";
    if (formData.serviceCities.length === 0)
      newErrors.serviceCities = "At least one city is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast("error", "Please fill all required fields correctly");
      return;
    }

    try {
      const payload = {
        client_name: formData.clientName,
        product_name: formData.productName,
        specific_requirement: formData.specificRequirement,
        short_code: formData.shortCode,
        official_email: formData.officialEmail,
        price: 0,
        client_poc: {
          person_name: formData.clientPocPersonName,
          person_designation: formData.clientPocPersonDesignation,
          phone: {
            country_code: "+91",
            mobile_number: formData.clientPocPhone,
          },
        },
        address_details: {
          address_line: formData.addressLine,
          city: formData.addressCity,
          state: formData.addressState,
          pincode: formData.addressPincode,
          country: formData.country,
        },
        gst_number: formData.gstNumber,
        aadhaar_number: formData.aadhaarNumber,
        pan_number: formData.panNumber,
        automate_qr_code: true,
        qr_code: "",
        bank_details: {
          bank_name: formData.bankName,
          bank_account_number: formData.accountNumber,
          ifsc_code: formData.ifscCode,
          branch: formData.branch,
        },
        tin_number: formData.tinNumber,
        category: formData.selectedSubCategories.map(
          (subCat) => `${subCat.name} - ${subCat.name2}`
        ),
        serviceAreas: {
          primary_services: formData.primaryServiceIds,
          categories: formData.selectedSubCategories.map((subCat) => ({
            category_id: subCat.category_id,
            payment_way: {
              isPayViaClient: subCat.payment_way.isPayViaClient,
              isPayViaCustomer: subCat.payment_way.isPayViaCustomer,
              ticketPicPrize:
                subCat.ticketPicPrize === ""
                  ? 0
                  : Number(subCat.ticketPicPrize),
            },
          })),
          states: formData.serviceStates,
          cities: formData.serviceCities,
          pincodes: formData.serviceAreas.pincodes,
        },
      };

      let response;
      if (isEditMode && editingClient) {
        // Update existing client
        response = await new AdminApi().updateClient(
          editingClient._id,
          payload
        );

        if (bulkPincodeFile) {
          await uploadServiceAreasFromFile(editingClient._id, bulkPincodeFile);
        }

        showToast("success", "Client updated successfully");
        if (onClientAdded) onClientAdded(); // Refresh the list
        if (onCancelEdit) onCancelEdit(); // Exit edit mode
      } else {
        // Create new client
        response = await new AdminApi().addClient(payload);

        if (bulkPincodeFile) {
          const createdClientId = extractClientIdFromResponse(response);
          if (createdClientId) {
            await uploadServiceAreasFromFile(createdClientId, bulkPincodeFile);
          } else {
            showToast(
              "error",
              "Client created, but bulk pincode upload was skipped (client id not found)"
            );
          }
        }

        showToast("success", "Client created successfully");
        setFormData(initialFormState);
        setBulkPincodeFile(null);
        setUploadedPincodeFileName("");
        setBulkUploadSummary(null);
        if (onClientAdded) onClientAdded(); // Refresh the list
      }
    } catch (err) {
      console.error("Error saving client:", err);
      showToast(
        "error",
        err?.response?.data?.message || "Failed to save client"
      );
    }
  };

  // Get available cities for current states
  const availableCities = getCitiesForStates(formData.serviceStateCodes);
  const allCityNames = availableCities.map((city) => city.name);
  const isAllCitiesSelected =
    formData.serviceCities.length === allCityNames.length &&
    allCityNames.length > 0;
  const isSomeCitiesSelected =
    formData.serviceCities.length > 0 &&
    formData.serviceCities.length < allCityNames.length;

  const isAllPincodesSelected =
    formData.serviceAreas.pincodes.length === pincodeOptions.length &&
    pincodeOptions.length > 0;
  const isSomePincodesSelected =
    formData.serviceAreas.pincodes.length > 0 &&
    formData.serviceAreas.pincodes.length < pincodeOptions.length;

  const isAllStatesSelected =
    formData.serviceStateCodes.length === indianStates.length;
  const isSomeStatesSelected =
    formData.serviceStateCodes.length > 0 &&
    formData.serviceStateCodes.length < indianStates.length;

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={toast.open}
        autoHideDuration={5000}
        onClose={closeToast}
      >
        <Alert
          onClose={closeToast}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {isFormVisible && (
        <FormContainer elevation={3}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ mb: 3, fontWeight: "bold" }}
          >
            {isEditMode ? "Edit Client" : "Add New Client"}
          </Typography>

          {isLoading.formData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography>Loading client data...</Typography>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Basic Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Client Name"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleChange}
                        error={!!errors.clientName}
                        helperText={errors.clientName}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Product Name"
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                        error={!!errors.productName}
                        helperText={errors.productName}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={!!errors.primaryServices}
                        sx={{ mb: 2 }}
                      >
                        <InputLabel id="primary-service-label">
                          Primary Services
                        </InputLabel>
                        <StyledSelect
                          sx={{ minWidth: 250 }}
                          labelId="primary-service-label"
                          id="primary-service-select"
                          multiple
                          value={formData.primaryServiceIds}
                          onChange={handlePrimaryServiceChange}
                          label="Primary Services *"
                          required
                          disabled={isLoading.primaryServices}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => {
                                const service = allPrimaryServices.find(
                                  (s) => s.id === value
                                );
                                const displayName =
                                  service?.name || String(value);
                                return <Chip key={value} label={displayName} />;
                              })}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 250,
                              },
                            },
                          }}
                        >
                          {allPrimaryServices.length > 0 ? (
                            allPrimaryServices.map((service) => (
                              <MenuItem
                                key={service.id}
                                value={service.id}
                                sx={{ whiteSpace: "normal" }}
                              >
                                <Checkbox
                                  checked={
                                    formData.primaryServiceIds.indexOf(
                                      service.id
                                    ) > -1
                                  }
                                />
                                <ListItemText primary={service.name} />
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              {isLoading.primaryServices
                                ? "Loading..."
                                : "No services available"}
                            </MenuItem>
                          )}
                        </StyledSelect>
                        {errors.primaryServices && (
                          <Typography variant="caption" color="error">
                            {errors.primaryServices}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Services Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Services
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  {formData.selectedSubCategories.map((subCat, index) => (
                    <Grid
                      container
                      spacing={2}
                      key={index}
                      sx={styles.serviceRow}
                    >
                      <Grid item xs={12} md={4}>
                        <FormControl
                          fullWidth
                          error={!!errors[`subCategory-${index}`]}
                        >
                          <InputLabel id={`subcategory-label-${index}`}>
                            Sub-Category
                          </InputLabel>
                          <StyledSelect
                            sx={{ minWidth: 250 }}
                            labelId={`subcategory-label-${index}`}
                            id={`subcategory-select-${index}`}
                            value={subCat.category_id}
                            onChange={(e) => handleSubCategoryChange(e, index)}
                            label="Sub-Category *"
                            required
                            disabled={isLoading.subCategories}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                            }}
                          >
                            {formData.subCategories.map((category) => (
                              <MenuItem
                                key={category._id}
                                value={category._id}
                                sx={styles.menuItem}
                              >
                                {category.name} - {category.name2} (₹
                                {category.price})
                              </MenuItem>
                            ))}
                          </StyledSelect>
                          {errors[`subCategory-${index}`] && (
                            <Typography variant="caption" color="error">
                              {errors[`subCategory-${index}`]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <StyledTextField
                          label="Price"
                          value={subCat.ticketPicPrize}
                          onChange={(e) => handlePriceChange(e, index)}
                          type="number"
                          required
                          error={!!errors[`price-${index}`]}
                          helperText={errors[`price-${index}`]}
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl component="fieldset" fullWidth>
                          <RadioGroup
                            row
                            aria-label="payment-method"
                            name={`payment-method-${index}`}
                            value={
                              subCat.payment_way?.isPayViaClient
                                ? "client"
                                : "customer"
                            }
                            onChange={(e) =>
                              handlePaymentMethodChange(e, index)
                            }
                          >
                            <FormControlLabel
                              value="client"
                              control={<Radio />}
                              label="Client Pays"
                            />
                            <FormControlLabel
                              value="customer"
                              control={<Radio />}
                              label="Customer Pays"
                            />
                          </RadioGroup>
                          {errors[`paymentMethod-${index}`] && (
                            <Typography variant="caption" color="error">
                              {errors[`paymentMethod-${index}`]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={1}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => removeSubCategory(index)}
                          sx={styles.removeButton}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addSubCategory}
                    disabled={formData.primaryServiceIds.length === 0}
                    sx={{ mt: 2 }}
                  >
                    Add Service
                  </Button>
                  {errors.subCategories && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {errors.subCategories}
                    </Typography>
                  )}
                </FormSectionBody>
              </FormSection>

              {/* Location Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Location Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth error={!!errors.serviceStates}>
                        <InputLabel id="service-states-label">
                          States *
                        </InputLabel>
                        <StyledSelect
                          sx={{ minWidth: 200 }}
                          labelId="service-states-label"
                          id="service-states-select"
                          multiple
                          value={formData.serviceStateCodes}
                          onChange={handleServiceStatesChange}
                          label="States *"
                          required
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => {
                                const state = indianStates.find(
                                  (s) => s.isoCode === value
                                );
                                const displayName =
                                  state?.name || String(value);
                                return <Chip key={value} label={displayName} />;
                              })}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 250,
                              },
                            },
                          }}
                        >
                          {/* Select All option for states */}
                          <MenuItem
                            value="select-all-states"
                            sx={styles.menuItem}
                          >
                            <Checkbox
                              checked={isAllStatesSelected}
                              indeterminate={isSomeStatesSelected}
                            />
                            <ListItemText primary="Select All States" />
                          </MenuItem>

                          {/* Clear All option for states */}
                          <MenuItem
                            value="clear-all-states"
                            sx={styles.menuItem}
                          >
                            <ListItemText primary="Clear All States" />
                          </MenuItem>

                          <Divider />

                          {indianStates.map((state) => (
                            <MenuItem
                              key={state.isoCode}
                              value={state.isoCode}
                              sx={styles.menuItem}
                            >
                              <Checkbox
                                checked={
                                  formData.serviceStateCodes.indexOf(
                                    state.isoCode
                                  ) > -1
                                }
                                onChange={handleStateCheckboxChange(
                                  state.isoCode
                                )}
                              />
                              <ListItemText primary={state.name} />
                            </MenuItem>
                          ))}
                        </StyledSelect>
                        {errors.serviceStates && (
                          <Typography variant="caption" color="error">
                            {errors.serviceStates}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Selected: {formData.serviceStateCodes.length} states
                          </Typography>
                        </Box>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth error={!!errors.serviceCities}>
                        <InputLabel id="service-cities-label">
                          Cities *
                        </InputLabel>
                        <StyledSelect
                          sx={{ minWidth: 200 }}
                          labelId="service-cities-label"
                          id="service-cities-select"
                          multiple
                          value={formData.serviceCities}
                          onChange={handleServiceCitiesChange}
                          label="Cities *"
                          required
                          disabled={formData.serviceStateCodes.length === 0}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip key={value} label={value} />
                              ))}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 250,
                              },
                            },
                          }}
                        >
                          {/* Select All option for cities */}
                          <MenuItem
                            value="select-all-cities"
                            sx={styles.menuItem}
                          >
                            <Checkbox
                              checked={isAllCitiesSelected}
                              indeterminate={isSomeCitiesSelected}
                            />
                            <ListItemText primary="Select All Cities" />
                          </MenuItem>

                          {/* Clear All option for cities */}
                          <MenuItem
                            value="clear-all-cities"
                            sx={styles.menuItem}
                          >
                            <ListItemText primary="Clear All Cities" />
                          </MenuItem>

                          <Divider />

                          {availableCities.map((city) => (
                            <MenuItem
                              key={city.name}
                              value={city.name}
                              sx={styles.menuItem}
                            >
                              <Checkbox
                                checked={
                                  formData.serviceCities.indexOf(city.name) > -1
                                }
                                onChange={handleCityCheckboxChange(city.name)}
                              />
                              <ListItemText primary={city.name} />
                            </MenuItem>
                          ))}
                        </StyledSelect>
                        {errors.serviceCities && (
                          <Typography variant="caption" color="error">
                            {errors.serviceCities}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Selected: {formData.serviceCities.length} cities
                            from {formData.serviceStateCodes.length} states
                          </Typography>
                        </Box>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="pincode-label">
                          Service Area Pincodes (Select multiple)
                        </InputLabel>
                        <StyledSelect
                          sx={{ minWidth: 200 }}
                          labelId="pincode-label"
                          id="pincode-select"
                          multiple
                          value={formData.serviceAreas.pincodes}
                          onChange={handlePincodeChange}
                          disabled={
                            formData.serviceCities.length === 0 ||
                            isLoading.pincodes
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip key={value} label={value} />
                              ))}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 250,
                              },
                            },
                          }}
                        >
                          {/* Select All option for pincodes */}
                          <MenuItem
                            value="select-all-pincodes"
                            sx={styles.menuItem}
                          >
                            <Checkbox
                              checked={isAllPincodesSelected}
                              indeterminate={isSomePincodesSelected}
                            />
                            <ListItemText primary="Select All Pincodes" />
                          </MenuItem>

                          {/* Clear All option for pincodes */}
                          <MenuItem
                            value="clear-all-pincodes"
                            sx={styles.menuItem}
                          >
                            <ListItemText primary="Clear All Pincodes" />
                          </MenuItem>

                          <Divider />

                          {isLoading.pincodes ? (
                            <MenuItem disabled>Loading pincodes...</MenuItem>
                          ) : pincodeOptions.length > 0 ? (
                            pincodeOptions.map((pincodeItem) => (
                              <MenuItem
                                key={pincodeItem._id}
                                value={pincodeItem.Pincode}
                                sx={styles.menuItem}
                              >
                                <Checkbox
                                  checked={formData.serviceAreas.pincodes.includes(
                                    pincodeItem.Pincode
                                  )}
                                  onChange={handlePincodeCheckboxChange(
                                    pincodeItem.Pincode
                                  )}
                                />
                                <ListItemText primary={pincodeItem.Pincode} />
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              {formData.serviceCities.length > 0
                                ? "No pincodes found for selected cities"
                                : "Select cities first"}
                            </MenuItem>
                          )}
                        </StyledSelect>
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="caption" color="textSecondary">
                            Selected: {formData.serviceAreas.pincodes.length}{" "}
                            pincodes
                          </Typography>
                          {formData.serviceCities.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              From: {formData.serviceCities.length} cities
                              across {formData.serviceStateCodes.length} states
                            </Typography>
                          )}
                        </Box>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Bulk Pincode Upload (.xlsx)
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mb: 2 }}
                        >
                          Excel format: first column header should be
                          &quot;pincode&quot;, then one pincode per row.
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <Button variant="outlined" component="label">
                            Choose File
                            <input
                              hidden
                              type="file"
                              accept=".xlsx"
                              onChange={handleBulkPincodeFileChange}
                            />
                          </Button>
                          <Button
                            variant="text"
                            onClick={handleDownloadPincodeSample}
                          >
                            Download Sample
                          </Button>

                          <Typography variant="body2" color="textSecondary">
                            {bulkPincodeFile?.name || "No file selected"}
                          </Typography>

                          {isEditMode && (
                            <Button
                              variant="contained"
                              onClick={() =>
                                uploadServiceAreasFromFile(
                                  editingClient?._id,
                                  bulkPincodeFile
                                )
                              }
                              disabled={!bulkPincodeFile || isUploadingPincodes}
                            >
                              {isUploadingPincodes
                                ? "Uploading..."
                                : "Upload Now"}
                            </Button>
                          )}
                        </Box>

                        {!isEditMode && bulkPincodeFile && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            sx={{ mt: 1 }}
                          >
                            Selected file will be uploaded automatically after
                            client creation.
                          </Typography>
                        )}

                        {uploadedPincodeFileName && (
                          <Typography
                            variant="caption"
                            color="success.main"
                            display="block"
                            sx={{ mt: 1 }}
                          >
                            Uploaded: {uploadedPincodeFileName}
                          </Typography>
                        )}

                        {bulkUploadSummary && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: "grey.100",
                            }}
                          >
                            <Typography variant="caption" display="block">
                              Total in file: {bulkUploadSummary.totalInExcel} |
                              Uploaded: {bulkUploadSummary.uploadedCount} |
                              Duplicates: {bulkUploadSummary.duplicateCount} |
                              Invalid: {bulkUploadSummary.invalidCount}
                            </Typography>
                            {bulkUploadSummary.uploadedPincodes.length > 0 && (
                              <Typography
                                variant="caption"
                                color="success.main"
                                display="block"
                              >
                                Uploaded Pincodes:{" "}
                                {bulkUploadSummary.uploadedPincodes.join(", ")}
                              </Typography>
                            )}
                            {bulkUploadSummary.duplicatePincodes.length > 0 && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                display="block"
                              >
                                Duplicate Pincodes:{" "}
                                {bulkUploadSummary.duplicatePincodes.join(", ")}
                              </Typography>
                            )}
                            {bulkUploadSummary.invalidPincodes.length > 0 && (
                              <Typography
                                variant="caption"
                                color="error"
                                display="block"
                              >
                                Invalid Pincodes:{" "}
                                {bulkUploadSummary.invalidPincodes.join(", ")}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Additional Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Additional Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Specific Requirement"
                        name="specificRequirement"
                        value={formData.specificRequirement}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Short Code"
                        name="shortCode"
                        value={formData.shortCode}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Contact Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Official Email"
                        name="officialEmail"
                        value={formData.officialEmail}
                        onChange={handleChange}
                        error={!!errors.officialEmail}
                        helperText={errors.officialEmail}
                        type="email"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Point of Contact Person Name"
                        name="clientPocPersonName"
                        value={formData.clientPocPersonName}
                        onChange={handleChange}
                        error={!!errors.clientPocPersonName}
                        helperText={errors.clientPocPersonName}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Point of Contact Person Designation"
                        name="clientPocPersonDesignation"
                        value={formData.clientPocPersonDesignation}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        label="Point of Contact Phone Number"
                        name="clientPocPhone"
                        value={formData.clientPocPhone}
                        onChange={handleChange}
                        error={!!errors.clientPocPhone}
                        helperText={errors.clientPocPhone}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>+91</Typography>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                        required
                      />
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Address Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Address Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <StyledTextField
                        sx={{ minWidth: 235 }}
                        label="Address"
                        name="addressLine"
                        value={formData.addressLine}
                        onChange={handleChange}
                        error={!!errors.addressLine}
                        helperText={errors.addressLine}
                        fullWidth
                        required
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth error={!!errors.addressState}>
                        <InputLabel id="address-state-label">
                          State *
                        </InputLabel>
                        <StyledSelect
                          sx={{ minWidth: 250 }}
                          labelId="address-state-label"
                          id="address-state-select"
                          label="State *"
                          value={formData.addressStateCode || ""}
                          onChange={handleAddressStateChange}
                          required
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 250,
                              },
                            },
                          }}
                        >
                          {indianStates.map((state) => (
                            <MenuItem
                              key={state.isoCode}
                              value={state.isoCode}
                              sx={{ whiteSpace: "normal" }}
                            >
                              {state.name}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                        {errors.addressState && (
                          <Typography variant="caption" color="error">
                            {errors.addressState}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="City"
                        name="addressCity"
                        value={formData.addressCity}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.addressCity}
                        helperText={errors.addressCity}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="Pincode"
                        name="addressPincode"
                        value={formData.addressPincode}
                        onChange={handleChange}
                        error={!!errors.addressPincode}
                        helperText={errors.addressPincode}
                        fullWidth
                        required
                        inputProps={{ maxLength: 6 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="Country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Financial Information Section */}
              <FormSection>
                <FormSectionHeader>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Financial Information
                  </Typography>
                </FormSectionHeader>
                <FormSectionBody>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="PAN Number"
                        name="panNumber"
                        value={formData.panNumber}
                        onChange={handleChange}
                        error={!!errors.panNumber}
                        helperText={errors.panNumber}
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="GST Number"
                        name="gstNumber"
                        value={formData.gstNumber}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="Bank Name"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                        error={!!errors.bankName}
                        helperText={errors.bankName}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="Account Number"
                        name="accountNumber"
                        value={formData.accountNumber}
                        onChange={handleChange}
                        error={!!errors.accountNumber}
                        helperText={errors.accountNumber}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="Branch"
                        name="branch"
                        value={formData.branch}
                        onChange={handleChange}
                        error={!!errors.branch}
                        helperText={errors.branch}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StyledTextField
                        label="IFSC Code"
                        name="ifscCode"
                        value={formData.ifscCode}
                        onChange={handleChange}
                        error={!!errors.ifscCode}
                        helperText={errors.ifscCode}
                        fullWidth
                        required
                      />
                    </Grid>
                  </Grid>
                </FormSectionBody>
              </FormSection>

              {/* Submit Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: 4,
                }}
              >
                {isEditMode && (
                  <Button
                    variant="outlined"
                    onClick={onCancelEdit}
                    size="large"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={styles.submitButton}
                >
                  {isEditMode ? "Update Client" : "Submit"}
                </Button>
              </Box>
            </form>
          )}
        </FormContainer>
      )}
    </Box>
  );
};

export default AddClient;
